import { h } from '../dom.js';
import { AudioPlayer, formatTime } from '../audio.js';

export interface ControlBarHandle {
  root: HTMLElement;
  update: () => void;
}

export interface ControlBarActions {
  onToggle: () => void;
  onPrevSentence: () => void;
  onNextSentence: () => void;
  onPrevCourse: () => void;
  onNextCourse: () => void;
  onSeekPercent: (pct: number) => void;
}

export function createControlBar(audio: AudioPlayer, actions: ControlBarActions): ControlBarHandle {
  const timeCur = h('span', { className: 'ctl-time__cur', text: '0:00' });
  const timeDur = h('span', { className: 'ctl-time__dur', text: '0:00' });

  const seekBar = h('div', { className: 'ctl-seek' });
  const seekFill = h('div', { className: 'ctl-seek__fill' });
  const seekKnob = h('div', { className: 'ctl-seek__knob' });
  seekBar.appendChild(seekFill);
  seekBar.appendChild(seekKnob);
  seekBar.addEventListener('pointerdown', (e) => {
    const rect = seekBar.getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      actions.onSeekPercent(pct);
    };
    move(e as PointerEvent);
    const up = (ev: PointerEvent) => {
      move(ev);
      window.removeEventListener('pointermove', up as never);
      window.removeEventListener('pointerup', up as never);
    };
    window.addEventListener('pointermove', move as never);
    window.addEventListener('pointerup', up as never);
  });

  const playBtn = h('button', {
    className: 'ctl-btn ctl-btn--primary',
    attrs: { type: 'button', 'aria-label': '播放/暂停' },
    on: { click: () => actions.onToggle() },
  }, [h('span', { className: 'ctl-btn__icon', text: '▶' })]);

  const prevSent = h('button', {
    className: 'ctl-btn',
    attrs: { type: 'button', 'aria-label': '上一句' },
    on: { click: () => actions.onPrevSentence() },
    text: '◀ 句',
  });
  const nextSent = h('button', {
    className: 'ctl-btn',
    attrs: { type: 'button', 'aria-label': '下一句' },
    on: { click: () => actions.onNextSentence() },
    text: '句 ▶',
  });
  const prevCourse = h('button', {
    className: 'ctl-btn',
    attrs: { type: 'button', 'aria-label': '上一课' },
    on: { click: () => actions.onPrevCourse() },
    text: '⏮ 课',
  });
  const nextCourse = h('button', {
    className: 'ctl-btn',
    attrs: { type: 'button', 'aria-label': '下一课' },
    on: { click: () => actions.onNextCourse() },
    text: '课 ⏭',
  });
  const root = h('div', { className: 'ctl' }, [
    h('div', { className: 'ctl-row ctl-row--seek' }, [
      timeCur,
      seekBar,
      timeDur,
    ]),
    h('div', { className: 'ctl-row ctl-row--main' }, [
      prevCourse,
      prevSent,
      playBtn,
      nextSent,
      nextCourse,
    ]),
  ]);

  function update(): void {
    (playBtn.querySelector('.ctl-btn__icon') as HTMLElement).textContent = audio.playing ? '⏸' : '▶';
    playBtn.classList.toggle('ctl-btn--playing', audio.playing);
    const dur = audio.duration;
    const cur = audio.currentTime;
    timeCur.textContent = formatTime(cur);
    timeDur.textContent = formatTime(dur);
    const pct = dur > 0 ? cur / dur : 0;
    seekFill.style.width = `${(pct * 100).toFixed(2)}%`;
    seekKnob.style.left = `${(pct * 100).toFixed(2)}%`;
  }

  update();
  return { root, update };
}
